import { SubscriptionStatusEnum } from 'modules/payment/subscriptions/enums/subscription-status-enum'
import SettingsLayout from 'modules/profile/components/settings-layout'
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys'
import { MANAGE_SUBSCRIPTIONS_API } from 'modules/profile/manage-subscriptions/api/cancel-subscriptions-api'
import CancelSubscriptionModal from 'modules/profile/manage-subscriptions/components/cancel-subscription-modal'
import UpdateSubscriptionFieldsModal from 'modules/profile/manage-subscriptions/components/update-subscription-fields-modal'
import { ManageSubscriptionsLoco } from 'modules/profile/manage-subscriptions/constants/manage-subscriptions-loco'
import { SubscriptionStatusUiEnum } from 'modules/profile/manage-subscriptions/enums/subscription-status-ui'
import { useManageSubscriptions } from 'modules/profile/manage-subscriptions/hooks/use-manage-subscriptions'
import { ManageSubscriptionsInterface } from 'modules/profile/manage-subscriptions/types/manage-subscriptions-interface'
import { getSubscriptionStatus } from 'modules/profile/manage-subscriptions/utils/get-subscription-status'
import { NextPage } from 'next'
import React from 'react'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { CheckCircleIcon, CloseCircleIcon, StatusCanceling } from '@systemeio/ui/icons'
import { Tooltip } from '@systemeio/ui'
import { PricePlanTooltip } from 'shared/components/tooltip/price-plan-tooltip'

const getStatusIcon = (subscription: ManageSubscriptionsInterface) => {
  const status = getSubscriptionStatus(subscription)

  if (status === SubscriptionStatusUiEnum.active || status === SubscriptionStatusUiEnum.completed) {
    return <CheckCircleIcon className={`fill-green h-[16px] w-[16px] sm:h-[20px] sm:w-[20px]`} />
  }

  if (status === SubscriptionStatusUiEnum.cancelled) {
    return <CloseCircleIcon className={`fill-danger h-[16px] w-[16px] sm:h-[20px] sm:w-[20px]`} />
  }

  if (status === SubscriptionStatusUiEnum.canceling) {
    return <StatusCanceling className={`h-[16px] w-[16px] sm:h-[20px] sm:w-[20px]`} />
  }
}

const ManageSubscriptions: NextPage = () => {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const { setPagination, pagination, setLimit, limit } = usePagination()

  const getStatusLabel = (subscription: ManageSubscriptionsInterface) => {
    const status = getSubscriptionStatus(subscription)

    if (status === SubscriptionStatusUiEnum.active) {
      return t('global.active')
    }

    if (status === SubscriptionStatusUiEnum.cancelled) {
      const deactivatedAt = subscription.subscriptionItems[0].deactivatedAt

      if (deactivatedAt !== null) {
        return `${t('global.cancelled')} - ${dateTimeFormat(deactivatedAt, {
          timeFormat: user?.timeFormat,
          dateFormat: user?.dateFormat,
          timeZone: user?.timezone,
        })}`
      }
      return t('global.cancelled')
    }

    if (status === SubscriptionStatusUiEnum.canceling) {
      return t('global.canceling')
    }

    if (status === SubscriptionStatusUiEnum.completed) {
      return t('dashboard.subscriptions.subscription_completed')
    }
  }

  const manageSubscriptionsColumns: ColumnDefinitionInterface<ManageSubscriptionsInterface>[] = [
    {
      key: 'subscriptionItems',
      header: t('dashboard.subscriptions.title_plural'),
      widthPercent: 30,
      onRender: subscription => (
        <PricePlanTooltip
          pricePlan={subscription.subscriptionItems[0].orderItem.pricePlan}
          hiddenInfo
        >
          <span className={'cursor-pointer'}>
            {subscription.subscriptionItems[0].orderItem.pricePlan.name}
          </span>
        </PricePlanTooltip>
      ),
    },
    {
      key: 'status',
      header: t('global.status'),
      widthPercent: 40,
      onRender: subscription => (
        <div className="flex items-center lg:ml-4">
          <Tooltip label={getStatusLabel(subscription)} mode="hover" key="remove-tooltip">
            {getStatusIcon(subscription)}
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: t('dashboard.subscriptions.date'),
      widthPercent: 30,
      onRender: subscription => (
        <span>
          {dateTimeFormat(subscription.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      ),
    },
  ]

  const { data: subscriptions, mutate } = useManageSubscriptions({ limit, pagination })

  const updateData = useUpdateData({ setPagination, api: MANAGE_SUBSCRIPTIONS_API })

  const onUnsubscribe = async (subscriptionItemId: number) => {
    await updateData(false, undefined, {
      mutationData: subscriptions,
      onMutate: async () => {
        await mutate(data => {
          if (data) {
            return {
              items: data.items.map(item => {
                const subscriptionItem = item.subscriptionItems.find(
                  el => el.id === subscriptionItemId,
                )

                if (subscriptionItem)
                  item = {
                    ...item,
                    subscriptionItems: item.subscriptionItems.map(el => {
                      if (el === subscriptionItem) {
                        return { ...el, status: SubscriptionStatusEnum.cancelled }
                      } else {
                        return el
                      }
                    }),
                  }
                return item
              }),
              hasMore: data.hasMore,
            }
          }
        }, false)
      },
    })
  }
  return (
    <SettingsLayout
      needSave={false}
      titles={[{ caption: t(settingsMenuLocoKeys.manage_subscriptions), isStatic: true }]}
    >
      <Table
        actions={{
          custom: [
            {
              name: 'cancel-subscription',
              shouldRender: subscription =>
                getSubscriptionStatus(subscription) === SubscriptionStatusUiEnum.active,
              caption: t('dashboard.settings.manage_subscriptions.cancel_subscription'),
              onActionRender: (opened, onClose, subscription, key) => (
                <CancelSubscriptionModal
                  key={key}
                  subscription={subscription}
                  opened={opened}
                  onClose={onClose}
                  onUnsubscribe={onUnsubscribe}
                />
              ),
            },
            {
              name: 'update-information',
              shouldRender: subscription =>
                getSubscriptionStatus(subscription) === SubscriptionStatusUiEnum.active,
              caption: t('dashboard.settings.manage_subscriptions.update_information'),
              onActionRender: (opened, onClose, subscription, key) => (
                <UpdateSubscriptionFieldsModal
                  opened={opened}
                  onClose={onClose}
                  subscriptionId={subscription.subscriptionItems[0].id}
                  key={key}
                />
              ),
            },
          ],
        }}
        data={subscriptions}
        columns={manageSubscriptionsColumns}
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
      />
    </SettingsLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(ManageSubscriptionsLoco, 'global.settings')

export default ManageSubscriptions
